import React, { useRef, useEffect, useCallback } from 'react';

interface FuzzyTextProps {
    children: string;
    fontSize?: string | number;
    fontWeight?: string | number;
    fontFamily?: string;
    color?: string;
    className?: string;
    enableHover?: boolean;
    baseIntensity?: number;
    hoverIntensity?: number;
    fuzzRange?: number;
    fps?: number;
    direction?: 'horizontal' | 'vertical' | 'both';
}

const FuzzyText: React.FC<FuzzyTextProps> = ({
    children,
    fontSize = 'clamp(2rem, 8vw, 8rem)',
    fontWeight = 900,
    fontFamily = 'inherit',
    color = 'currentColor',
    className = '',
    enableHover = true,
    baseIntensity = 0.18,
    hoverIntensity = 0.5,
    fuzzRange = 30,
    fps = 30,
    direction = 'both',
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef(children);
    const isHoveredRef = useRef(false);
    const animationFrameRef = useRef<number>();
    const lastFrameTimeRef = useRef(0);

    const getResolvedStyles = useCallback(() => {
        if (!containerRef.current) return { color: '#ffffff', fontSize: 48, fontWeight: '900', fontFamily: 'sans-serif' };

        const computedStyle = window.getComputedStyle(containerRef.current);
        const resolvedColor = color === 'currentColor' ? computedStyle.color : color;
        const resolvedFontSize = typeof fontSize === 'number'
            ? fontSize
            : parseFloat(computedStyle.fontSize);

        return {
            color: resolvedColor,
            fontSize: resolvedFontSize,
            fontWeight: String(fontWeight),
            fontFamily: fontFamily === 'inherit' ? computedStyle.fontFamily : fontFamily,
        };
    }, [color, fontSize, fontWeight, fontFamily]);

    const drawFuzzyText = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const styles = getResolvedStyles();
        const intensity = isHoveredRef.current ? hoverIntensity : baseIntensity;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Set font
        ctx.font = `${styles.fontWeight} ${styles.fontSize * dpr}px ${styles.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const text = textRef.current;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        // Measure text for character-by-character rendering
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;

        // Draw each character with random offset for fuzzy effect
        let currentX = centerX - textWidth / 2;

        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            const charMetrics = ctx.measureText(char);
            const charWidth = charMetrics.width;

            let offsetX = 0;
            let offsetY = 0;

            if (direction === 'horizontal' || direction === 'both') {
                offsetX = (Math.random() - 0.5) * fuzzRange * intensity * dpr;
            }
            if (direction === 'vertical' || direction === 'both') {
                offsetY = (Math.random() - 0.5) * fuzzRange * intensity * dpr;
            }

            ctx.fillStyle = styles.color;
            ctx.fillText(char, currentX + charWidth / 2 + offsetX, centerY + offsetY);

            currentX += charWidth;
        }
    }, [getResolvedStyles, hoverIntensity, baseIntensity, fuzzRange, direction]);

    const animate = useCallback((timestamp: number) => {
        const frameInterval = 1000 / fps;

        if (timestamp - lastFrameTimeRef.current >= frameInterval) {
            drawFuzzyText();
            lastFrameTimeRef.current = timestamp;
        }

        animationFrameRef.current = requestAnimationFrame(animate);
    }, [fps, drawFuzzyText]);

    const setupCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const dpr = window.devicePixelRatio || 1;
        const styles = getResolvedStyles();

        // Create temporary context to measure text
        const tempCtx = canvas.getContext('2d');
        if (!tempCtx) return;

        tempCtx.font = `${styles.fontWeight} ${styles.fontSize}px ${styles.fontFamily}`;
        const textMetrics = tempCtx.measureText(textRef.current);

        // Calculate dimensions with padding for fuzzy effect
        const padding = fuzzRange * hoverIntensity * 2;
        const width = textMetrics.width + padding;
        const height = styles.fontSize * 1.5 + padding;

        // Set canvas size with device pixel ratio
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        drawFuzzyText();
    }, [getResolvedStyles, fuzzRange, hoverIntensity, drawFuzzyText]);

    useEffect(() => {
        textRef.current = children;
        setupCanvas();
    }, [children, setupCanvas]);

    useEffect(() => {
        setupCanvas();
        animationFrameRef.current = requestAnimationFrame(animate);

        const handleResize = () => setupCanvas();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [setupCanvas, animate]);

    const handleMouseEnter = () => {
        if (enableHover) {
            isHoveredRef.current = true;
        }
    };

    const handleMouseLeave = () => {
        if (enableHover) {
            isHoveredRef.current = false;
        }
    };

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                fontSize: typeof fontSize === 'number' ? `${fontSize}px` : fontSize,
                fontWeight,
                fontFamily,
                color,
                display: 'inline-block',
                lineHeight: 1,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <canvas
                ref={canvasRef}
                style={{
                    display: 'block',
                    maxWidth: '100%',
                }}
            />
        </div>
    );
};

export default FuzzyText;
